import { useCallback, useEffect, useRef, useState } from 'react';
import { IProcessedMeasurement } from 'app/types/measurement';
import { IWebSocketMeasurementConfig } from '../components/form/types';
import { useWebSocketMeasurementService } from './useWebSocketMeasurementService';
import { Telemetry } from 'app/services/telemetry/Telemetry';

const DEFAULT_TIMEOUT_MS = 30_000;

interface IUseFlowWizardWebSocketParams {
  webSocketConfig?: IWebSocketMeasurementConfig;
  steps: any[];
  onNavigateToResult: (stepIndex: number) => void;
}

export const useFlowWizardWebSocket = ({
  webSocketConfig,
  steps,
  onNavigateToResult,
}: IUseFlowWizardWebSocketParams) => {
  const [isWaitingForMeasurement, setIsWaitingForMeasurement] = useState(false);
  const [isTimedOut, setIsTimedOut] = useState(false);
  const [receivedMeasurement, setReceivedMeasurement] = useState<IProcessedMeasurement | null>(null);
  const hasAutoStarted = useRef(false);
  const listeningStartCountRef = useRef(-1);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Always call unconditionally — WebSocketMeasurementProvider is mounted above all tab screens.
  const webSocketService = useWebSocketMeasurementService();

  const clearMeasurementTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const startMeasurementTimeout = useCallback(() => {
    clearMeasurementTimeout();
    const timeoutMs = webSocketConfig?.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    timeoutRef.current = setTimeout(() => {
      setIsWaitingForMeasurement(false);
      setIsTimedOut(true);
      Telemetry.event('measurement.flow.timeout', {
        domain: 'measurement',
        activityName: webSocketConfig?.activityName,
        timeoutMs,
      }, 'warning');
    }, timeoutMs);
  }, [webSocketConfig?.activityName, webSocketConfig?.timeoutMs, clearMeasurementTimeout]);

  useEffect(() => {
    if (webSocketConfig?.autoStart && !hasAutoStarted.current && !isWaitingForMeasurement) {
      hasAutoStarted.current = true;
      listeningStartCountRef.current = webSocketService.measurements.length;
      setIsWaitingForMeasurement(true);
      setIsTimedOut(false);
      if (!webSocketService.isConnected) webSocketService.connect();
      startMeasurementTimeout();
    }
  }, [webSocketConfig?.autoStart, isWaitingForMeasurement, webSocketService, startMeasurementTimeout]);

  useEffect(() => {
    if (!isWaitingForMeasurement || !webSocketConfig) return;

    const hasNew = webSocketService.measurements.length > listeningStartCountRef.current;
    if (!hasNew) return;

    const latest = webSocketService.measurements[0];
    if (!latest || latest.activityName !== webSocketConfig.activityName) return;

    clearMeasurementTimeout();
    setReceivedMeasurement(latest);
    setIsWaitingForMeasurement(false);
    setIsTimedOut(false);

    const resultKey = webSocketConfig.resultStepKey ?? 'measurementResultStep';
    const resultStepIndex = steps.findIndex(s => s.key === resultKey);

    webSocketConfig.onMeasurementReceived?.(latest, resultStepIndex !== -1 ? steps[resultStepIndex] : null);
    if (resultStepIndex !== -1) onNavigateToResult(resultStepIndex);
  }, [webSocketService.measurements, isWaitingForMeasurement, webSocketConfig, steps, onNavigateToResult, clearMeasurementTimeout]);

  useEffect(() => () => clearMeasurementTimeout(), [clearMeasurementTimeout]);

  const startWebSocketListening = useCallback(() => {
    if (!webSocketConfig || isWaitingForMeasurement) return;
    listeningStartCountRef.current = webSocketService.measurements.length;
    setIsWaitingForMeasurement(true);
    setIsTimedOut(false);
    if (!webSocketService.isConnected) webSocketService.connect();
    startMeasurementTimeout();
  }, [webSocketConfig, isWaitingForMeasurement, webSocketService, startMeasurementTimeout]);

  const retryWebSocketListening = startWebSocketListening;

  const shouldStartListeningOnNext = useCallback((currentStepKey: string): boolean => {
    if (!webSocketConfig || isWaitingForMeasurement) return false;
    const beforeKey = webSocketConfig.beforeMeasurementStepKey ?? 'beforeMeasurementStep';
    return currentStepKey === beforeKey;
  }, [webSocketConfig, isWaitingForMeasurement]);

  return {
    isWaitingForMeasurement,
    isTimedOut,
    receivedMeasurement,
    startWebSocketListening,
    retryWebSocketListening,
    shouldStartListeningOnNext,
    webSocketService,
  };
};
